import React, { useState } from 'react';
import { Box, Stack } from '@mui/material';
import { useWebLocale } from '../../../../contexts/WebLocaleContext';
import { useUserStore } from '../../../../stores/userStore';
import Rating from '../../../../components/common/Rating';
import CharacterCounter from '../../../../components/common/CharacterCounter';
import BasicButton from '../../../../components/button/BasicButton';
import CustomTextareaAutosize from '../../../../components/common/CustomTextareaAutosize';
import theme from '../../../../theme';
import UserWithProfile from '../../../../components/common/UserWithProfile';
import CancelConfirmDialog from '../../../../components/dialog/CancelConfirmDialog';

const REVIEW_MAX_LENGTH = 10000;

const MyReviewForm = ({
  myReview,
  onSubmit,
  onCancel,
  isEditing = false,
  editFormData,
  setEditFormData,
  totalReviewCount = 0,
}) => {
  const { locale } = useWebLocale();
  const user = useUserStore((state) => state.user);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editFormData.rating || !editFormData.content.trim()) return;

    try {
      await onSubmit(editFormData);
    } catch (error) {
      console.error('Failed to perform review operation:', error);
    }
  };

  const handleCancelClick = () => {
    if (isEditing && myReview) {
      const hasChanges =
        editFormData.rating !== myReview.rating ||
        editFormData.content.trim() !== myReview.content.trim();

      if (hasChanges) {
        setIsCancelDialogOpen(true);
      } else {
        onCancel();
      }
    } else {
      onCancel();
    }
  };

  const handleCancelConfirm = () => {
    setIsCancelDialogOpen(false);
    onCancel();
  };

  const handleCancelCancel = () => {
    setIsCancelDialogOpen(false);
  };

  return (
    <Box
      className="pp-review-compose"
      component="form"
      onSubmit={handleSubmit}
      sx={{ px: { xs: 2, lg: 2.5 }, py: 2.5, bgcolor: theme.palette.grey.bg }}
    >
      {/* 리뷰 헤더 */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <UserWithProfile
          username={user.nickname}
          profileImageUrl={user.profileImageUrl}
          userNickname={user.nickname}
          grade={user.grade}
          spacing={1}
        />
        <Box
          sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, flex: 1 }}
        >
          <Rating
            value={editFormData.rating}
            onChange={(newValue) => setEditFormData({ ...editFormData, rating: newValue })}
          />
        </Box>
      </Stack>

      {/* 리뷰 작성 */}

      <CustomTextareaAutosize
        minRows={{ xs: 4, md: 10 }}
        aria-label={locale === 'en' ? 'Album review' : '앨범 리뷰 내용'}
        placeholder={
          !isEditing && !myReview && totalReviewCount === 0
            ? '첫 리뷰를 남겨주세요.'
            : '앨범에 대한 리뷰를 작성해 주세요.'
        }
        value={editFormData.content}
        onChange={(e) => setEditFormData({ ...editFormData, content: e.target.value })}
        maxLength={REVIEW_MAX_LENGTH}
        sx={{ marginBottom: '8px' }}
      />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{
          position: { md: 'sticky' },
          bottom: { md: 12 },
          bgcolor: 'var(--pp-surface)',
          border: '1px solid var(--pp-border)',
          borderRadius: 1,
          p: 1.5,
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', md: 'auto' },
            display: 'flex',
            justifyContent: { xs: 'flex-end', md: 'flex-start' },
          }}
        >
          <CharacterCounter
            currentLength={editFormData.content?.length || 0}
            maxLength={REVIEW_MAX_LENGTH}
          />
        </Box>
        <Box display="flex" gap={1} width={{ xs: '100%', md: 'auto' }}>
          {isEditing && (
            <BasicButton
              variant="outlined"
              type="button"
              onClick={handleCancelClick}
              sx={{
                width: { xs: '100%', md: 'auto' },
                minWidth: { md: '70px', lg: '58px' },
                minHeight: '44px',
                padding: '8px 16px',
                typography: 'body3',
                fontWeight: 'medium',
              }}
            >
              취소
            </BasicButton>
          )}
          <BasicButton
            variant="contained"
            type="submit"
            disabled={!editFormData.rating || !editFormData.content.trim()}
            sx={{
              width: { xs: '100%', md: 'auto' },
              minWidth: { md: '70px', lg: '58px' },
              minHeight: '44px',
              padding: '8px 16px',
              typography: 'body3',
              fontWeight: 'medium',
              color: theme.palette.primary.contrastText,
            }}
          >
            {isEditing ? '수정' : '등록'}
          </BasicButton>
        </Box>
      </Stack>

      {/* 취소 확인 다이얼로그 */}
      <CancelConfirmDialog
        open={isCancelDialogOpen}
        onClose={handleCancelCancel}
        onConfirm={handleCancelConfirm}
      />
    </Box>
  );
};

export default MyReviewForm;
